<template>
  <dialog-form
    :show-dialog="isShowDialog"
    :is-disabled="status.isLoadingOptions || !!pendingItem"
    @cancel="modalClose"
    @submit="modalSubmit"
  >
    <template #title>
      Item Options
    </template>
    <div
      v-loading="status.isLoadingOptions"
      class="dialog-body"
      element-loading-text="Loading..."
      element-loading-custom-class="custom-spinner-class"
    >
      <div
        v-if="item.item"
        class="item-header pb-3 mb-4"
      >
        <h4 class="mt-0">
          {{ item.item.title }}
        </h4>
        <p v-if="item.item.subtitle">
          {{ item.item.subtitle }}
        </p>
        <div><b>Price:</b> ${{ (item.item.price / 100).toFixed(2) }}</div>
        <div><b>Restaurant:</b> {{ item.item.storeTitle }}</div>
        <div class="service-name">
          <b>Service:</b> {{ item.item.service }}
        </div>
      </div>
      <div v-if="item.options">
        <div v-if="item.options == 0">
          No additional options to select. Please continue by clicking "Add Item".
        </div>
        <store-item-options
          v-show="item.options.length"
          :options="item.options"
          :item="item"
          :show-errors="status.showOptionsErrors"
          @loading="status.isLoadingOptions = $event"
        />
      </div>

      <div
        v-if="item.item"
        class="item-quantity mt-4"
      >
        <div class="row align-items-center mb-0">
          <label class="col-sm-3 control-label"> Quantity: </label>
          <div class="col-sm-9">
            <el-input
              v-model.number="item.item.quantity"
              type="number"
              class="input-amount"
              style="width: 150px"
              step="1"
              min="1"
            />
          </div>
        </div>
      </div>

      <div
        v-if="item.item && item.item.service === 'doordash'"
        class="mt-4"
      >
        <div class="row align-items-center mb-0">
          <label class="col-sm-3 control-label"> If sold out: </label>
          <div class="col-sm-9">
            <el-select v-model="item.item.substitutionPreference">
              <el-option
                v-for="preference in substitutionPreferences"
                :key="preference.value"
                :value="preference.value"
                :label="preference.label"
              />
            </el-select>
          </div>
        </div>
      </div>
      <div
        v-if="item.item && !isOptionInstructionsDisabled"
        class="mt-4"
      >
        <div class="row align-items-center mb-0">
          <label class="col-sm-3 control-label"> Option Instruction: </label>
          <div class="col-sm-9">
            <el-input
              v-model="specialInstructions"
              type="textarea"
              class="option-instruction"
              :rows="2"
              placeholder="Item option instructions"
              @input="onInputSpecialInstructions"
            />
          </div>
        </div>
      </div>

      <message-html :result-html="status.optionsSaveError" />
    </div>
    <div v-show="pendingItem">
      Your cart is being recalculated, please wait before adding another item.
    </div>
    <template #submit-button>
      {{ isItemUpdate ? 'Save Options' : 'Add Item' }}
    </template>
  </dialog-form>
</template>

<script lang="ts">
import { computed, reactive, ref } from 'vue';

import StoreItemOptions from '../Operator/Delivery/StoreItemOptions.vue';

const substitutionPreferences = [
  {
    value: 'substitute',
    label: 'Go with Merchant recommendation',
  },
  {
    value: 'contact',
    label: 'Contact us',
  },
  {
    value: 'refund',
    label: 'Refund item',
  },
  {
    value: 'cancel',
    label: 'Cancel the entire order',
  },
];

export default {
  name: 'DeliveryItemDetailsDialog',

  components: {
    StoreItemOptions,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    pendingItem: {
      type: Object,
      required: false,
      default: null,
    },
    isItemUpdate: {
      type: Boolean,
      default: false,
    },
    isShowDialog: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['submit', 'cancel'],

  setup: (props: any, context: any) => {
    const { emit } = context;
    const specialInstructions = ref('');
    const status = reactive({
      optionsSaveError: '',
      isLoadingOptions: false,
      showOptionsErrors: false,
    });

    const isOptionInstructionsDisabled = computed(() => props.item?.item?.isSpecialInstructionsDisabled);

    const onInputSpecialInstructions = (value: any) => {
      props.item.value.item.specialInstructions = value;
    };

    const modalClose = () => {
      emit('cancel');
    };

    const countSelectedOptions = (selectedValues: any) => {
      let count = 0;

      for (const key in selectedValues) {
        const value = selectedValues[key];

        if (value) {
          count++;
        }
      }

      return count;
    };

    const countSelected = (selectedValues: any) => {
      let count = 0;

      for (const key in selectedValues) {
        const value = selectedValues[key];

        if (typeof value === 'boolean' && value) {
          count++;
        } else {
          count += value;
        }
      }

      return count;
    };

    const checkRequirement = (itemOptions: any) => {
      for (const option of itemOptions) {
        if (option.type === 'input') {
          const selectedAmount = countSelected(option.selectedValues);

          if (selectedAmount > option.maxAggregateOptionsQuantity) {
            return false;
          }

          if (selectedAmount < option.minAggregateOptionsQuantity) {
            return false;
          }
        }

        const selectedOptionCount = countSelectedOptions(option.selectedValues);

        if (selectedOptionCount > option.max) {
          return false;
        }

        if (selectedOptionCount < option.min) {
          return false;
        }

        for (const value of option.values) {
          if (value.subOptions && option.selectedValues && option.selectedValues[value.id]) {
            const isMatchedSubOptions = checkRequirement(value.subOptions);

            if (!isMatchedSubOptions) {
              return false;
            }
          }
        }
      }

      return true;
    };

    const modalSubmit = () => {
      const itemValue = props.item;
      const isMatchedRequirement = checkRequirement(itemValue.options);

      if (!isMatchedRequirement) {
        return (status.showOptionsErrors = true);
      }

      emit('submit');
    };

    return {
      status,
      specialInstructions,
      onInputSpecialInstructions,
      isOptionInstructionsDisabled,
      substitutionPreferences,
      modalClose,
      modalSubmit,
    };
  },
};
</script>
